import { useEffect, useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Loading } from '../Loading';

interface DataPoolCreateProps {
  onSubmit: () => void;
}

const GB_TO_MB = 1024;

export const DataPoolCreate = ({ onSubmit }: DataPoolCreateProps) => {
  const api = useApi();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [entityId, setEntityId] = useState<string | ''>('');
  const [carrier, setCarrier] = useState('');
  const [monthlyAllowance, setMonthlyAllowance] = useState('');
  const [resetDay, setResetDay] = useState('1');
  const [alertEnable, setAlertEnable] = useState(true);
  const [threshold, setThreshold] = useState('');
  const [throttleEnable, setThrottleEnable] = useState(false);
  const [throttleRate, setThrottleRate] = useState('');

  const { isLoading, data: entitiesData } = useQuery(
    ['entities'],
    api.getEntityApiV1GetEntityGet.bind(null, {}),
    { enabled: true },
  );

  const { isLoading: isCarriersLoading, data: carriersData } = useQuery(
    ['cellularCarriers'],
    api.cellularCarrierApiV1CellularCarrierGet,
    { enabled: true },
  );

  const createDataPool = useMutation(api.createDataPoolApiV1CreateDataPoolPost, {
    onSuccess() {
      toast.success(`Created Shared Data Pool "${name}" successfully.`);
      onSubmit();
    },
    onError: apiError,
  });

  useEffect(() => {
    if (carriersData?.data.length && !carrier) {
      setCarrier(carriersData.data[0]);
    }
  }, [carriersData, carrier]);

  const handleSubmit = () => {
    createDataPool.mutate({
      data_pool_name: name,
      description: description || undefined,
      entity_id: entityId || undefined,
      carrier: carrier as any,
      data_allowance_mb: Number(monthlyAllowance) * GB_TO_MB,
      reset_day: Number(resetDay),
      alert_enable: alertEnable,
      alert_threshold_percent: alertEnable ? Number(threshold) : undefined,
      throttle_enable: alertEnable && throttleEnable,
      throttle_throughput_mbps: alertEnable && throttleEnable ? Number(throttleRate) : undefined,
    });
  };

  const isValid =
    name.trim() !== '' &&
    carrier !== '' &&
    monthlyAllowance !== '' &&
    Number(monthlyAllowance) > 0 &&
    resetDay !== '' &&
    Number(resetDay) !== 0 &&
    Number(resetDay) >= -28 &&
    Number(resetDay) <= 28 &&
    (!alertEnable ||
      (threshold !== '' &&
        Number(threshold) >= 0 &&
        Number(threshold) <= 100)) &&
    (!alertEnable || !throttleEnable || (throttleRate !== '' && Number(throttleRate) > 0));

  if (isLoading || isCarriersLoading) return <Loading />;

  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Name"
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="entity-label">Entity</InputLabel>
            <Select
              labelId="entity-label"
              label="Entity"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {entitiesData?.data.map((entity) => (
                <MenuItem value={entity.entity_id} key={entity.entity_id}>
                  {entity.entity_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="carrier-label">Carrier</InputLabel>
            <Select
              labelId="carrier-label"
              label="Carrier"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
            >
              {carriersData?.data.map((c) => (
                <MenuItem value={c} key={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            type="number"
            label="Monthly Data Allowance (GB)"
            variant="outlined"
            value={monthlyAllowance}
            onChange={(e) => setMonthlyAllowance(e.target.value)}
            inputProps={{ min: 0.1, step: 0.1 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            type="number"
            label="Data Reset Day"
            variant="outlined"
            value={resetDay}
            onChange={(e) => setResetDay(e.target.value)}
            inputProps={{ min: -28, max: 28, step: 1 }}
            helperText="Day of month usage resets (1–28). Negative counts from end: -1 = last day."
          />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={2} justifyContent="space-between">
            <FormControlLabel
              sx={{ flexShrink: 0, mr: 0 }}
              control={
                <Checkbox
                  checked={alertEnable}
                  onChange={(e) => {
                    setAlertEnable(e.target.checked);
                    if (!e.target.checked) setThrottleEnable(false);
                  }}
                />
              }
              label="Alert when usage exceeds _% of quota"
            />
            <TextField
              fullWidth
              type="number"
              label="Threshold (%)"
              variant="outlined"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              inputProps={{ min: 0, max: 100 }}
              disabled={!alertEnable}
              sx={{ maxWidth: 200 }}
            />
          </Box>
        </Grid>
        {alertEnable && (
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={2} justifyContent="space-between">
              <FormControlLabel
                sx={{ flexShrink: 0, mr: 0, pl: 3 }}
                control={
                  <Checkbox
                    checked={throttleEnable}
                    onChange={(e) => setThrottleEnable(e.target.checked)}
                  />
                }
                label="Enable data throttling to rate"
              />
              <TextField
                fullWidth
                type="number"
                label="mbps"
                variant="outlined"
                value={throttleRate}
                onChange={(e) => setThrottleRate(e.target.value)}
                inputProps={{ min: 0.1, step: 0.1 }}
                disabled={!throttleEnable}
                sx={{ maxWidth: 200 }}
              />
            </Box>
          </Grid>
        )}
      </Grid>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={!isValid || createDataPool.isLoading}>
          Create
        </Button>
      </Box>
    </Box>
  );
};

DataPoolCreate.displayName = 'Data Pool Create';
